import { executeFinanceCommand } from "@/features/finance/lib/retry-safe-command";
import { z } from "zod";
import type { Json } from "@/db/types";
import { purchaseOrderTotals } from "@/features/purchase-orders/hooks/purchase-order-totals";
import {
  DEFAULT_PURCHASE_ORDER_SORT,
  isPurchaseOrderSortField,
  type PurchaseOrderSort,
} from "@/features/purchase-orders/lib/purchase-order-sort";
import type {
  CreatePurchaseOrderPayload,
  CreatePurchaseOrderResult,
  CreateVendorPaymentPayload,
  CreateVendorPaymentResult,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderFilters,
  PurchaseOrderProduct,
  PurchaseOrderProductLine,
  TransferPurchaseOrderProductRequest,
  TransferPurchaseOrderProductResult,
  UpdatePurchaseOrderPayload,
  UpdatePurchaseOrderProductPayload,
  VendorPayment,
} from "@/features/purchase-orders/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import {
  applyFinanceDocumentSearch,
  applyFinanceListPage,
  archiveFinanceDocument,
  type FinanceDocumentChildDescriptor,
  financeDocumentQuery,
  type FinanceListPage,
  readFinanceDocumentChildren,
  resolveFinanceListPage,
  withAppWriteOrigin,
} from "@/lib/db/finance-documents";
import { tiebreakListOrder } from "@/lib/db/list-order";
import { DOCUMENT_TOTAL_FIELDS, fetchListTotals } from "@/lib/db/list-totals";
import { updateProductRecord } from "@/lib/db/products";
import { PRODUCT_SELECT } from "@/lib/db/selects";
import {
  cast,
  firstRecord,
  getOptionalString,
  isRecord,
  normalizeDateOnlyInput,
  toNumber,
  toPostgrestDayUntil,
  toRangeEndDay,
} from "@/lib/utils/query-helpers";

const backend = getBackendClient();
const createIdempotencyKeys = new Map<string, string>();

const VENDOR_PAYMENT_SELECT = `
  id, uid, amount, date, method, reference, purchase_order_id, account_id, created_at,
  gl_accounts!gl_vendor_payments_account_id_fkey ( id, name, uid ),
  gl_purchase_orders!gl_vendor_payments_purchase_order_id_fkey ( id, uid )
`;

const PRODUCT_STOCK_VIEW = "product_stock";

/**
 * Line projection for the purchase-order detail view.
 *
 * `product_stock` re-exposes every `gl_products` column, so this is the
 * purchase-order line set plus the issued position. The editor refuses
 * quantity and cost restatement once a lot has been invoiced or sampled,
 * because both are the basis of everything priced from it.
 */
const PURCHASE_ORDER_PRODUCT_STOCK_SELECT = `
  ${PRODUCT_SELECT.trim()},
  qty_sampled, qty_sold
`;

/**
 * `product_stock` is a view and the generated `Database` type carries tables
 * only, so `DataClient.from` cannot name it. The cast is confined to this one
 * function rather than loosening the client for every caller.
 */
function fromProductStock(): QueryBuilder<PurchaseOrderProduct> {
  return (backend.data.from as unknown as (table: string) => QueryBuilder<PurchaseOrderProduct>)(
    PRODUCT_STOCK_VIEW,
  );
}

const vendorCreditApplicationTotalsSchema = z.array(
  z.object({
    amount_applied: z.union([z.number(), z.string()]).nullish(),
    is_reversal: z.boolean().nullish(),
  }),
);

/**
 * Purchase orders carry a unit count alongside the money, and "how many units
 * are on order" is the second question this list answers.
 */
const PURCHASE_ORDER_TOTAL_FIELDS = [...DOCUMENT_TOTAL_FIELDS, "total_quantity"] as const;

export type PurchaseOrderListTotals = Record<(typeof PURCHASE_ORDER_TOTAL_FIELDS)[number], number>;

function normalizeDateInput(date?: string | null): string {
  if (!date) {
    return new Date().toISOString();
  }
  return date.includes("T") ? date : `${date}T00:00:00.000Z`;
}

async function ensureVendorAccount(accountId: string): Promise<string | null> {
  const { data, error } = await backend.data
    .from("gl_accounts")
    .select("id, is_vendor")
    .eq("id", accountId)
    .single();

  if (error) {
    return error.message;
  }
  if (!data?.is_vendor) {
    return "Selected account must be a vendor.";
  }
  return null;
}

async function getCompletePurchaseOrder(id: string): Promise<PurchaseOrder> {
  const { data, error } = await financeDocumentQuery("purchase_order").eq("id", id).single();

  if (error) {
    throw new Error(error.message);
  }
  if (!isRecord(data)) {
    throw new Error("Purchase order response was invalid.");
  }

  return { ...cast<PurchaseOrder>(data), id };
}

async function fetchVendorPaymentById(id: string): Promise<VendorPayment> {
  const { data: payment, error: paymentError } = await backend.data
    .from("gl_vendor_payments")
    .select(VENDOR_PAYMENT_SELECT)
    .eq("id", id)
    .single();

  if (paymentError) {
    throw new Error(paymentError.message);
  }

  return cast<VendorPayment>(payment);
}

function normalizeVendorPaymentSplitResult(
  value: unknown,
  defaults?: {
    productId?: string | null;
    purchaseOrderId?: string;
  },
): {
  paymentId: string | null;
  split: CreateVendorPaymentResult["split"];
} {
  const raw = firstRecord(value);
  if (!raw) {
    return {
      paymentId: null,
      split: null,
    };
  }

  const paymentId = getOptionalString(raw, "payment_id") ?? getOptionalString(raw, "paymentId");
  const vendorCreditId =
    getOptionalString(raw, "vendor_credit_id") ?? getOptionalString(raw, "vendorCreditId");

  return {
    paymentId,
    split: {
      payment_id: paymentId,
      vendor_credit_id: vendorCreditId,
      requested_amount:
        toNumber(raw.requested_amount) ?? toNumber(raw.requestedAmount) ?? undefined,
      applied_amount: toNumber(raw.applied_amount) ?? toNumber(raw.appliedAmount) ?? undefined,
      remainder_amount:
        toNumber(raw.remainder_amount) ?? toNumber(raw.remainderAmount) ?? undefined,
      purchase_order_id:
        getOptionalString(raw, "purchase_order_id") ??
        getOptionalString(raw, "purchaseOrderId") ??
        defaults?.purchaseOrderId,
      product_id:
        getOptionalString(raw, "product_id") ??
        getOptionalString(raw, "productId") ??
        defaults?.productId ??
        null,
    },
  };
}

async function buildVendorPaymentResult(
  splitValue: unknown,
  defaults?: {
    productId?: string | null;
    purchaseOrderId?: string;
  },
): Promise<CreateVendorPaymentResult> {
  const normalizedSplit = normalizeVendorPaymentSplitResult(splitValue, defaults);
  if (!normalizedSplit.paymentId) {
    return {
      payment: null,
      split: normalizedSplit.split,
    };
  }

  try {
    return {
      payment: await fetchVendorPaymentById(normalizedSplit.paymentId),
      split: normalizedSplit.split,
    };
  } catch {
    return {
      payment: null,
      split: normalizedSplit.split,
    };
  }
}

async function applyVendorPaymentAndFetch(params: {
  purchase_order_id: string;
  amount: number;
  date?: string;
  method?: CreateVendorPaymentPayload["method"];
  reference?: string | null;
  product_id?: string | null;
}): Promise<CreateVendorPaymentResult> {
  const { purchase_order_id, amount, date, method, reference, product_id } = params;

  const command = await executeFinanceCommand("vendor_payment", {
    document_id: purchase_order_id,
    amount,
    date: date ? normalizeDateInput(date) : null,
    method: method || "Cash",
    reference: reference || null,
    product_id: product_id || null,
  });

  const result = await buildVendorPaymentResult(command.result, {
    purchaseOrderId: purchase_order_id,
    productId: product_id ?? null,
  });
  command.acknowledge();
  return result;
}

/**
 * The column the list is ordered by, confined to the declared allowlist.
 *
 * Header clicks arrive here as a URL-supplied string, so an unrecognised field
 * falls back to the default order instead of reaching `order`.
 */
function resolvePurchaseOrderSort(params: PurchaseOrderFilters): PurchaseOrderSort {
  const field = params.sort_field;
  if (field && isPurchaseOrderSortField(field)) {
    return {
      direction: params.sort_direction === "asc" ? "asc" : "desc",
      field,
    };
  }
  return DEFAULT_PURCHASE_ORDER_SORT;
}

function buildBasePurchaseOrderQuery(page: FinanceListPage, sort: PurchaseOrderSort) {
  return applyFinanceListPage(
    financeDocumentQuery("purchase_order").order(sort.field, {
      ascending: sort.direction === "asc",
    }),
    page,
  );
}

type PurchaseOrderListQuery = ReturnType<typeof buildBasePurchaseOrderQuery>;

/**
 * Same-day merge previews must resolve the exact row
 * `xan_create_or_attach_document` would attach to, which orders by
 * `created_at DESC NULLS LAST, id DESC`. Every other caller keeps the plain
 * date ordering.
 *
 * Only the `created_at` half lives here. `id DESC` is the tiebreaker every
 * paged list query ends with and is appended after this one, so the
 * merge-target key still decides the rows it exists to decide.
 */
function applyMergeTargetOrdering(query: PurchaseOrderListQuery): PurchaseOrderListQuery {
  return query.order("created_at", { ascending: false, nullsFirst: false });
}

function applyPurchaseOrderScalarFilters(
  query: PurchaseOrderListQuery,
  params: PurchaseOrderFilters,
): PurchaseOrderListQuery {
  let next = query;
  if (params.status) {
    next = next.eq("payment_status", params.status);
  }
  // The scope chips narrow to a set of statuses; the select above narrows to
  // one inside it. Both hold at once, so both are applied.
  if (params.status_in && params.status_in.length > 0) {
    next = next.in("payment_status", params.status_in);
  }
  if (params.account_id) {
    next = next.eq("account_id", params.account_id);
  }
  if (params.date_from) {
    next = next.gte("date", params.date_from.split("T")[0]);
  }
  if (params.date_to) {
    next = next.lt("date", toPostgrestDayUntil(params.date_to));
  }
  if (params.merge_date) {
    // An equality, like `getInvoicePreviewMergeTarget`: the create attaches to
    // a document stamped exactly at this day's midnight and to nothing later in
    // the day, so a day window here would preview the wrong order. The day is
    // read as `createPurchaseOrder` reads it, so the lookup names the day the
    // create will.
    next = next.eq("date", normalizeDateOnlyInput(params.merge_date));
  }
  return next;
}

export async function getPurchaseOrders(params?: PurchaseOrderFilters): Promise<PurchaseOrder[]> {
  const filters = params ?? {};
  const baseQuery = buildBasePurchaseOrderQuery(
    resolveFinanceListPage(filters, 50),
    resolvePurchaseOrderSort(filters),
  );

  const scoped = tiebreakListOrder(
    filters.merge_target_order ? applyMergeTargetOrdering(baseQuery) : baseQuery,
  );
  // Free-text search runs over the `search_text` computed column through the
  // shared port predicate — the same SQL `purchase_order_list_totals` filters
  // with, so the footer and the rows cannot select different sets.
  const query = applyFinanceDocumentSearch(
    applyPurchaseOrderScalarFilters(scoped, filters),
    filters.search,
  );

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return cast<PurchaseOrder[]>(data || []);
}

/**
 * Count, money totals and unit count for the whole filtered set, from the
 * server. Takes the same filters `getPurchaseOrders` applies.
 */
export async function getPurchaseOrderTotals(
  params?: PurchaseOrderFilters,
): Promise<PurchaseOrderListTotals> {
  return fetchListTotals(
    "purchase_order_list_totals",
    {
      p_account_id: params?.account_id || null,
      p_date_from: params?.date_from?.split("T")[0] || null,
      p_date_to: params?.date_to ? toRangeEndDay(params.date_to) : null,
      p_search: params?.search?.trim() || null,
      p_status: params?.status || null,
      p_statuses: params?.status_in?.length ? params.status_in : null,
    },
    PURCHASE_ORDER_TOTAL_FIELDS,
  );
}

/**
 * Child relations of the purchase-order detail read, resolved by the shared
 * port. The tables are purchase-order-specific — including the `product_stock`
 * view read — but the fan-out, scoping, and first-error semantics are not.
 */
const PURCHASE_ORDER_DETAIL_CHILDREN: readonly FinanceDocumentChildDescriptor[] = [
  {
    alsoEq: [["is_deleted", false]],
    from: () => fromProductStock().select(PURCHASE_ORDER_PRODUCT_STOCK_SELECT),
    order: { ascending: true, column: "created_at" },
    parentColumn: "purchase_order_id",
  },
  {
    from: () => backend.data.from("gl_vendor_payments").select(VENDOR_PAYMENT_SELECT),
    order: { ascending: false, column: "date" },
    parentColumn: "purchase_order_id",
  },
  {
    from: () =>
      backend.data.from("gl_vendor_credit_applications").select("amount_applied, is_reversal"),
    order: { ascending: false, column: "applied_at" },
    parentColumn: "purchase_order_id",
  },
];

export async function getPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
  const purchaseOrderPromise = getCompletePurchaseOrder(id);
  const childrenPromise = readFinanceDocumentChildren(id, PURCHASE_ORDER_DETAIL_CHILDREN);
  let purchaseOrderResult: Awaited<ReturnType<typeof getCompletePurchaseOrder>>;
  try {
    purchaseOrderResult = await purchaseOrderPromise;
  } catch (cause) {
    void childrenPromise.catch(() => undefined);
    throw cause;
  }
  const [productRows, paymentRows, creditApplicationRows] = await childrenPromise;

  const products = cast<PurchaseOrderProduct[]>(productRows ?? []);
  const payments = cast<VendorPayment[]>(paymentRows ?? []);
  const vendorCreditApplications = vendorCreditApplicationTotalsSchema.parse(
    creditApplicationRows ?? [],
  );
  const purchaseOrderTotalsResult = purchaseOrderTotals({
    purchaseOrder: purchaseOrderResult,
    payments,
    vendorCreditApplications,
  });

  return {
    purchase_order: {
      ...purchaseOrderResult,
      ...purchaseOrderTotalsResult,
    },
    products,
    payments,
  };
}

export async function createPurchaseOrder(
  payload: CreatePurchaseOrderPayload,
): Promise<CreatePurchaseOrderResult> {
  const vendorValidationError = await ensureVendorAccount(payload.account_id);
  if (vendorValidationError) {
    throw new Error(vendorValidationError);
  }

  const targetDate = normalizeDateOnlyInput(payload.date ?? null);
  const createFingerprint = JSON.stringify(payload);
  // A form rebuilds its payload on every submit, so retry identity follows its
  // validated values rather than object identity. Keep a failed create key;
  // delete it only once all canonical follow-up reads succeed.
  const idempotencyKey = createIdempotencyKeys.get(createFingerprint) ?? crypto.randomUUID();
  createIdempotencyKeys.set(createFingerprint, idempotencyKey);

  const { data: createResult, error: createError } = await backend.data.rpc(
    "xan_create_purchase_order_with_payload",
    {
      p_account_id: payload.account_id,
      p_date: targetDate,
      p_notes: payload.notes || undefined,
      p_products: payload.products as unknown as Json,
      p_initial_payment: (payload.initial_payment || null) as Json | null,
      p_idempotency_key: idempotencyKey,
    },
  );

  if (createError) {
    throw new Error(createError.message);
  }

  const normalizedCreateResult = Array.isArray(createResult)
    ? (createResult as unknown[])[0]
    : createResult;

  if (!isRecord(normalizedCreateResult)) {
    throw new Error("Purchase order create RPC returned an invalid response.");
  }

  const poId =
    typeof normalizedCreateResult.purchase_order_id === "string"
      ? normalizedCreateResult.purchase_order_id
      : null;

  if (!poId) {
    throw new Error("Purchase order ID was not returned.");
  }

  const mergedIntoExisting = normalizedCreateResult.merged_into_existing === true;
  const initialPaymentValue = normalizedCreateResult.initial_payment ?? null;
  const initialPaymentResult =
    initialPaymentValue === null
      ? null
      : await buildVendorPaymentResult(initialPaymentValue, {
          purchaseOrderId: poId,
        });

  const completePurchaseOrderResult = await getCompletePurchaseOrder(poId);

  const result = {
    purchase_order: completePurchaseOrderResult,
    merged_into_existing: mergedIntoExisting,
    initial_payment: initialPaymentResult,
  };
  createIdempotencyKeys.delete(createFingerprint);
  return result;
}

export async function updatePurchaseOrder(
  id: string,
  payload: UpdatePurchaseOrderPayload,
): Promise<PurchaseOrder> {
  if (payload.account_id) {
    const vendorValidationError = await ensureVendorAccount(payload.account_id);
    if (vendorValidationError) {
      throw new Error(vendorValidationError);
    }
  }

  const updates: Record<string, unknown> = {};
  if (payload.account_id !== undefined) {
    updates.account_id = payload.account_id;
  }
  if (payload.date !== undefined) {
    updates.date = payload.date;
  }
  if (payload.notes !== undefined) {
    updates.notes = payload.notes;
  }

  const { error } = await backend.data
    .from("gl_purchase_orders")
    .update(withAppWriteOrigin(updates))
    .eq("id", id);

  if (error) {
    throw new Error(error.message);
  }

  return getCompletePurchaseOrder(id);
}

export function deletePurchaseOrder(id: string): Promise<{ success: boolean; archive_id: string }> {
  return archiveFinanceDocument("purchase_order", id);
}

export async function createVendorPayment(
  payload: CreateVendorPaymentPayload,
): Promise<CreateVendorPaymentResult> {
  const { data: purchaseOrder, error: purchaseOrderError } = await backend.data
    .from("gl_purchase_orders")
    .select("id, account_id")
    .eq("id", payload.purchase_order_id)
    .single();

  if (purchaseOrderError) {
    throw new Error(purchaseOrderError.message);
  }
  if (!purchaseOrder?.account_id) {
    throw new Error("Purchase order account was not found.");
  }

  const vendorValidationError = await ensureVendorAccount(purchaseOrder.account_id);
  if (vendorValidationError) {
    throw new Error(vendorValidationError);
  }

  return applyVendorPaymentAndFetch({
    purchase_order_id: payload.purchase_order_id,
    amount: payload.amount,
    date: payload.date,
    method: payload.method,
    reference: payload.reference,
    product_id: payload.product_id,
  });
}

export async function transferPurchaseOrderProduct(
  request: TransferPurchaseOrderProductRequest,
): Promise<TransferPurchaseOrderProductResult> {
  if (request.source_purchase_order_id === request.target_purchase_order_id) {
    throw new Error("Target purchase order must be different from source.");
  }

  const { data, error } = await backend.data.rpc("xan_transfer_po_product_with_payments", {
    p_from_po_id: request.source_purchase_order_id,
    p_product_id: request.product_id,
    p_to_po_id: request.target_purchase_order_id,
    p_payment_handling: request.payment_handling,
  });

  if (error) {
    throw new Error(error.message);
  }

  const result = Array.isArray(data) ? (data as unknown[])[0] : data;
  if (!isRecord(result) || result.success !== true) {
    throw new Error("Product transfer failed.");
  }

  return {
    success: true,
    product_id: request.product_id,
    target_purchase_order_id: request.target_purchase_order_id,
    payment_handling: request.payment_handling,
    associated_payment_count: Number(result.associated_payment_count || 0),
    moved_payment_count: Number(result.moved_payment_count || 0),
    deleted_payment_count: Number(result.deleted_payment_count || 0),
  };
}

function namedProductLines(products: PurchaseOrderProductLine[]): PurchaseOrderProductLine[] {
  return products.filter((product) => Boolean(product.vendor_product_name?.trim()));
}

function readInsertedCount(value: unknown): number {
  const result = firstRecord(value);
  if (!result) {
    throw new Error("Add purchase order products RPC returned an invalid response.");
  }

  return toNumber(result.inserted_count) ?? 0;
}

/**
 * Attaches product lines to a saved purchase order.
 *
 * `gl_products.purchase_order_id` is `NOT NULL` because Postgres owns product
 * creation: the RPC shares its insert body with
 * `xan_create_purchase_order_with_payload`, so payload normalization, UID and
 * SKU derivation, and the order's recalculation happen once, server-side.
 * A row with no vendor product name names nothing that can be received, so it
 * is dropped before the payload is built.
 */
export async function addPurchaseOrderProducts(
  purchaseOrderId: string,
  products: PurchaseOrderProductLine[],
): Promise<{ inserted_count: number }> {
  const namedProducts = namedProductLines(products);
  if (namedProducts.length === 0) {
    throw new Error("Add at least one product");
  }

  const { data, error } = await backend.data.rpc("xan_add_purchase_order_products", {
    p_purchase_order_id: purchaseOrderId,
    p_products: namedProducts as unknown as Json,
  });

  if (error) {
    throw new Error(error.message);
  }

  return { inserted_count: readInsertedCount(data) };
}

/**
 * Restates one saved purchase-order line.
 *
 * The write goes through the shared product allowlist, so the trigger-derived
 * columns (`uid`, `sku`, `total_cost`) and the order's totals can never be
 * submitted — they are re-read from the server instead.
 */
export function updatePurchaseOrderProduct(
  productId: string,
  payload: UpdatePurchaseOrderProductPayload,
): Promise<PurchaseOrderProduct> {
  return updateProductRecord<PurchaseOrderProduct>(productId, payload);
}

/**
 * Soft-deletes one purchase-order line and recalculates the order.
 *
 * The RPC refuses the removal while `gl_vendor_payments` rows reference the
 * line, and its message is the operator-facing refusal directing them to
 * Transfer, which owns payment handling — so it is surfaced unchanged.
 */
export async function removePurchaseOrderProduct(
  purchaseOrderId: string,
  productId: string,
): Promise<{ success: true }> {
  const { error } = await backend.data.rpc("xan_remove_purchase_order_product", {
    p_purchase_order_id: purchaseOrderId,
    p_product_id: productId,
  });

  if (error) {
    throw new Error(error.message);
  }

  return { success: true };
}
